import { FC, useEffect, useState } from "react";
import { Box, Picture, Flex, Icon } from "@smile2impress/components/relaunch";
import { InvisalignLogoLightIcon } from "@smile2impress/components";
import { useCampaignContext } from "context/campaignContext";

import { TestIds } from "constants/test-ids";

import { MainBannerProps } from "./MainBanner.types";
import { MainBannerHeader } from "./components";
import { MainBannerAdvantages } from "./components/MainBannerAdvantages";
import { MainBannerSlogans } from "./components/MainBannerSlogans";

export const MainBanner: FC<MainBannerProps> = ({
    content: {
        title,
        subtitle,
        text,
        cta,
        advantages,
        slogans,
        image,
        extraStyles,
        headingTextStyle,
        invisalign = false,
        forceNoExtraContent,
    },
    headingAs,
}) => {
    const { campaign } = useCampaignContext();
    const [bannerHeight, setBannerHeight] = useState(0);

    useEffect(() => {
        if (campaign) setBannerHeight(36); // 36 is the height of mobile top promo banner in px
    }, [campaign]);

    const hasExtraContent =
        forceNoExtraContent === true
            ? false
            : Boolean(advantages?.length) ||
              Array.isArray(slogans) ||
              Boolean(invisalign);

    const mobileGradient =
        "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(8, 8, 8, 0.4) 100%)";
    const desktopGradient = hasExtraContent
        ? "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(8, 8, 8, 0.3) 100%)"
        : "none";

    return (
        <Box as="section" position="relative" width="100%" overflow="hidden">
            <Box px={[4]} mb={[4]} display={["none", "none", "block"]}>
                <MainBannerHeader
                    extraStyles={extraStyles}
                    headingAs={headingAs}
                    headingTextStyle={headingTextStyle}
                    title={title}
                    subtitle={subtitle}
                    text={text}
                    cta={cta}
                    withImage={!!image}
                    dataTestId={TestIds.MAIN_BANNER_HEADER_CTA_DESKTOP}
                />
            </Box>
            <Flex
                position="relative"
                bgColor={["black", "black", "initial"]}
                sx={{
                    minHeight: image
                        ? [
                              `calc(100vh - 62px - ${bannerHeight}px)`,
                              `calc(100vh - 62px - ${bannerHeight}px)`,
                              "0px",
                          ]
                        : "0px",
                    "@supports (height: 100svh)": {
                        minHeight: image
                            ? [
                                  `calc(100svh - 62px - ${bannerHeight}px)`,
                                  `calc(100svh - 62px - ${bannerHeight}px)`,
                                  "0px",
                              ]
                            : "0px",
                    },
                }}
            >
                <Box
                    position="absolute"
                    display={["block", "block", "none"]}
                    left="50%"
                    bottom="0"
                    width="430px"
                    height="750px"
                    transform="translateX(-50%)"
                    bgColor="base.gunmetal"
                >
                    <Picture
                        {...image}
                        imageProps={{
                            objectPosition: "center center",
                            objectFit: "fill",
                        }}
                        fetchPriority="high"
                    />
                </Box>

                <Box
                    w="100%"
                    h={10}
                    display={["none", "none", "block"]}
                    bgColor="base.gunmetal"
                >
                    <Picture {...image} fetchPriority="high" />
                </Box>

                <Flex
                    bgImage={[mobileGradient, mobileGradient, desktopGradient]}
                    top="50%"
                    bottom="0"
                    left="0"
                    right="0"
                    zIndex={0}
                    position={["absolute"]}
                ></Flex>
                <Flex
                    direction="column"
                    position={["relative", "relative", "absolute"]}
                    top="0"
                    bottom="0"
                    left="0"
                    right="0"
                    zIndex={1}
                    w="100%"
                    px={[2, 2, 4]}
                    pt={[3, 3, 4]}
                    pb={[3, 3, 4]}
                    justifyContent={
                        hasExtraContent ? "space-between" : "flex-end"
                    }
                    alignSelf={
                        hasExtraContent
                            ? ["unset", "unset", "flex-end"]
                            : ["flex-end"]
                    }
                >
                    {hasExtraContent && (
                        <Flex display={["flex", "flex", "none"]}>
                            <MainBannerHeader
                                extraStyles={extraStyles}
                                headingAs={headingAs}
                                headingTextStyle={headingTextStyle}
                                title={title}
                                subtitle={subtitle}
                                text={text}
                                cta={cta}
                                withImage={!!image}
                                dataTestId={
                                    TestIds.MAIN_BANNER_HEADER_CTA_MOBILE
                                }
                            />
                        </Flex>
                    )}
                    <Flex direction="row" gap={[1, 1, 0]}>
                        {hasExtraContent && (
                            <>
                                {advantages && advantages.length > 0 && (
                                    <MainBannerAdvantages
                                        advantages={advantages}
                                    />
                                )}
                                {invisalign && (
                                    <Icon
                                        as={InvisalignLogoLightIcon}
                                        w={["157px", "157px", "215px"]}
                                        h={["41px", "41px", "56px"]}
                                    />
                                )}
                                <Box maxWidth={["75%"]}>
                                    <MainBannerSlogans slogans={slogans} />
                                </Box>
                            </>
                        )}
                        {!hasExtraContent && (
                            <Flex
                                display={["flex", "flex", "none"]}
                                alignSelf="flex-end"
                            >
                                <MainBannerHeader
                                    extraStyles={extraStyles}
                                    headingAs={headingAs}
                                    headingTextStyle={headingTextStyle}
                                    title={title}
                                    subtitle={subtitle}
                                    text={text}
                                    cta={cta}
                                    withImage={!!image}
                                />
                            </Flex>
                        )}
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};
